// Browser side of voice messages: the microphone through `MediaRecorder`
// (Opus where the browser has it, else AAC in MP4) with an `AnalyserNode`
// measuring loudness for the live and sent waveforms.
import { clipContentType, LEVEL_INTERVAL_MS, recorderType, rmsLevel, VOICE_BITS_PER_SECOND, waveformOf } from "./voice";
import { finalizeWebm } from "./webm";

export interface RecordedClip {
  blob: Blob;
  /** `audio/webm`, `audio/ogg` or `audio/mp4`. */
  contentType: string;
  /** Time spent recording. */
  elapsedMs: number;
  waveform: number[];
}

/** A recorded WebM with the sizes, Duration and Cues players need to seek (Firefox writes none). */
async function seekable(blob: Blob, contentType: string): Promise<Blob> {
  if (contentType !== "audio/webm") return blob;
  const finalized = finalizeWebm(new Uint8Array(await blob.arrayBuffer()));
  return finalized ? new Blob([finalized as Uint8Array<ArrayBuffer>], { type: contentType }) : blob;
}

/** The type this browser records voice clips in, or null when it can't (no microphone API, no supported container). */
export function voiceRecorderType(): string | null {
  if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) return null;
  return recorderType((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * One recording from the microphone. `start` asks for it (the browser may
 * prompt); `stop` resolves with the clip, `cancel` throws it away. Either
 * releases the microphone.
 */
export class VoiceRecording {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private context: AudioContext | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private readonly chunks: Blob[] = [];
  private readonly levels: number[] = [];
  private startedAt = 0;
  private done = false;

  /** `onLevel` gets every loudness sample (RMS 0–1), `onFailed` a recorder error after the start. */
  constructor(
    private readonly type: string,
    private readonly onLevel: (levels: readonly number[], elapsedMs: number) => void,
    private readonly onFailed: (error: unknown) => void,
  ) {}

  async start(): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    if (this.done) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.stream = stream;
    try {
      const recorder = new MediaRecorder(stream, { mimeType: this.type, audioBitsPerSecond: VOICE_BITS_PER_SECOND });
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };
      recorder.onerror = (event) => {
        if (this.done) return;
        this.release();
        this.onFailed((event as Event & { error?: unknown }).error ?? new Error("recorder"));
      };
      this.recorder = recorder;
      this.measure(stream);
      recorder.start();
      this.startedAt = performance.now();
    } catch (error) {
      this.release();
      throw error;
    }
  }

  /** Time recorded so far, or null before the microphone started. */
  elapsed(): number | null {
    return this.recorder && this.startedAt ? performance.now() - this.startedAt : null;
  }

  /** Stops and resolves with the clip (rejects if the recorder made nothing usable). */
  stop(): Promise<RecordedClip> {
    const recorder = this.recorder;
    if (this.done || !recorder || recorder.state === "inactive") {
      this.release();
      return Promise.reject(new Error("Not recording"));
    }
    const elapsedMs = performance.now() - this.startedAt;
    this.done = true;
    return new Promise<RecordedClip>((resolve, reject) => {
      recorder.onstop = () => {
        const contentType = clipContentType(recorder.mimeType, this.type);
        this.release();
        const blob = new Blob(this.chunks, { type: contentType ?? "" });
        if (!contentType || blob.size === 0) {
          reject(new Error("Recording didn't work"));
          return;
        }
        seekable(blob, contentType).then(
          (clip) => resolve({ blob: clip, contentType, elapsedMs, waveform: waveformOf(this.levels) }),
          reject,
        );
      };
      // Takes the last levels, then hands over the final data and fires `stop`.
      this.sample();
      recorder.stop();
    });
  }

  cancel(): void {
    this.done = true;
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.onstop = null;
      this.recorder.ondataavailable = null;
      this.recorder.stop();
    }
    this.release();
  }

  private analyser: AnalyserNode | null = null;
  private samples: Uint8Array<ArrayBuffer> | null = null;

  private measure(stream: MediaStream): void {
    // The clock (and the length limit) runs even when Web Audio doesn't.
    this.timer = setInterval(() => this.sample(), LEVEL_INTERVAL_MS);
    try {
      const context = new AudioContext();
      this.context = context;
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      context.createMediaStreamSource(stream).connect(analyser);
      this.analyser = analyser;
      this.samples = new Uint8Array(analyser.fftSize);
      // Created inside a click, but some browsers still start it suspended.
      void context.resume().catch(() => undefined);
    } catch {
      // Without Web Audio the clip still records, with a flat waveform.
    }
  }

  private sample(): void {
    if (this.analyser && this.samples) {
      this.analyser.getByteTimeDomainData(this.samples);
      this.levels.push(rmsLevel(this.samples));
    }
    this.onLevel(this.levels, performance.now() - this.startedAt);
  }

  private release(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.analyser = null;
    this.samples = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    void this.context?.close().catch(() => undefined);
    this.context = null;
  }
}
